import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import dayjs from "dayjs";
import { useTranslation } from "@lib/i18n";
import { useDispatch } from "react-redux";
import { array, bool, func } from "prop-types";
import { useRouter } from "next/router";
import getConfig from "next/config";

import DealSummary from "@components/DealSummary/DealSummary";
import ContentWrapper from "@components/shared/ContentWrapper";
import ErrorBoundary from "@components/shared/ErrorBoundary";
import SimpleButton from "@components/shared/SimpleButton";
import StickyRequestButton from "@components/shared/StickyRequestButton";
import { Button } from "@components/shared/Button";
import PriceDetail from "@components/PriceDetail/PriceDetail";
import Delivery from "../Delivery/Delivery";
import Financing from "../Financing/Financing";
import ShareConfigModal from "./ShareConfigModal";
import VehicleRegistration from "../VehicleRegistration/VehicleRegistration";
import BasketHeader from "./BasketHeader";
import ProgressBar from "./ProgressBar";
import RedirectBrandIdForms from "components/RedirectBrandIdForms";
import UserDetailsContainer from "@components/UserDetails/Module";
import DiscountStripe from "@components/DiscountStripe/DiscountStripe";
import CollapsableContainer from "@components/shared/CollapsableContainer";

import gtm from "@gtm/core";
import useBasketState from "@hooks/useBasketState";
import api from "@api";
import { getExpectedDeliveryDate } from "@lib/getLeadTimeInfo";
import { getFinanceVariant } from "@lib/getFinanceVariant";
import {
  BRANDID_REGISTRATION_TYPES,
  COUNTRIES,
  CTA_SOL_TYPES,
  DEAL_STATES,
  DEAL_TYPE,
  DELIVERY_TYPE,
  REQUEST_ERROR_CODES,
  STEP_INDEXES,
  VEHICLE_REGISTRATION_METHOD,
} from "@shared/constants";
import { revertProgressEvent } from "@gtm/events/shared";
import { useFormattedDeal } from "@hooks/useFormattedDeal";
import { STEPS } from "@gtm/constants";
import { useIsUserTrustedSelector } from "@redux/reducers/auth";
import { useCurrentFinancingData } from "@hooks/financing/useCurrentFinancingData";
import { useDefaultPreconfiguration } from "@hooks/useDefaultPreconfiguration";
import { setIsRedirectedFromBrandIdAction } from "@redux/actions/deprecatedActions";
import { useRemoveQueryParameters } from "@hooks/useRemoveQueryParameters";
import { useStepAwareCashData } from "@hooks/financing/useStepAwareCashData";
import { useGetErrorMessage } from "@hooks/useGetErrorMessage";
import { getBasketUpdateUrl } from "gtm/core/utils";
import { setIsTrustedAction } from "@redux/actions/authActions";
import { getIsDeliveryPriceIncluded } from "@lib/getIsDeliveryPriceIncluded";
import { useCarValidation } from "@hooks/useCarValidation";
import { ROUTES } from "@shared/routing/routes";
import { useConfigurationPathname } from "@hooks/useConfigurationPathname";
import { useRoutingUtils } from "@hooks/useRoutingUtils";
import { useDetectDevice } from "@hooks/useDetectDevice";
import { useIsB2BSelected } from "@hooks/useIsB2BSelected";
import { useIsIndirectSalesEnabledSelector } from "@redux/reducers/appConfig";
import { useIsCashJourneySelected } from "@hooks/useIsCashJourneySelected";
import { useIsLLDJourneySelected } from "@hooks/useIsLLDJourneySelected";
import { useIsSCFJourneySelected } from "@hooks/useIsSCFJourneySelected";
import { useGetBrandIdRedirect } from "@hooks/useGetBrandIdRedirect";
import { removeBasketErrorAction } from "@redux/actions/errorsActions";
import { useIsRedirectedFromBrandIdSelector } from "@redux/reducers/deprecated";
import { useJourneySpecificScrappage } from "@hooks/useJourneySpecificScrappage";
import { getUrlWithoutTrailingSlash } from "@shared/v2/lib/getUrlWithoutTrailingSlash";
import { useGetAvailableRegistrationMethods } from "@hooks/useAvailableRegistrationMethods";
import { getCulture } from "@shared/v2/lib/getCulture";
import { getSolJourneyConfigForJourney } from "@shared/v2/lib/getSolJourneyConfigForJourney";
import { useGetPriceDetailProps } from "@hooks/useGetPriceDetailProps";
import { useIsAllowedToContinueWithOrder } from "@hooks/useIsAllowedToContinueWithOrder";

import {
  basketRegistrationChangeEvent,
  shareConfigurationEmailEvent,
} from "@gtm/events/basket";

import {
  useBasketErrorCodesSelector,
  useFinanceWidgetErrorsSelector,
} from "@redux/reducers/errors";

import {
  useBusinessModelSelector,
  useCarConfigurationSelector,
  useCurrentDealStateSelector,
  useDealSelector,
  useIsB2BLeasysSelected,
  useLastCtaSelector,
  useSelectedJourneySelector,
  useTokenSelector,
  useVehicleQuantitySelector,
} from "@redux/reducers/deal";

import {
  calculatePricesAction,
  createNewDealFromExistingAction,
  fetchAccessoriesIncreaseAction,
  patchUserProfileAction,
  postDealAction,
  resetDealAction,
  setIndirectSalesAction,
  setIsDealSavedByUserAction,
  setVehicleRegistrationDataAction,
  updateCarPreconfigurationAction,
  updateIsSelectionConfirmedAction,
  updateLastCtaAction,
} from "@redux/actions/dealActions";

import {
  useAppConfigSelector,
  useBrandIdSettingsSelector,
  useCarSelector,
  useCurrencySettingsSelector,
  useDateFormatSelector,
  useDiscountStripeSelector,
  useEcoBonusSelector,
  useIsAnyCEESubsidyEnabledForJourney,
  useIsGuestCheckoutEnabledSelector,
  useIsUIv2Selector,
  useMovesProgramSelector,
  useReferrerSelector,
} from "@redux/reducers/appConfig";
import { useCashVehiclePrice } from "hooks/useCashVehiclePrice";
import { getIsCountry } from "lib/getIsCountry";
import { useIsFixed } from "@hooks/useIsFixed";
import UserAuthentification from "./UserAuthentification";
import { twMerge } from "tailwind-merge";
import { FIXED_ELEMENTS_ATTRIBUTE_NAME } from "@hooks/useScrollToView";
import RegistrationCostChangedModal from "@components/shared/RegistrationCostChangedModal";

const { publicRuntimeConfig } = getConfig();

const { COUNTRY } = publicRuntimeConfig;

const Basket = ({ onReturnButtonClick, deliveryPoints, isSSRRendered }) => {
  const isCashJourneySelected = useIsCashJourneySelected();
  const isLLDJourneySelected = useIsLLDJourneySelected();
  const isSCFJourneySelected = useIsSCFJourneySelected();
  const [initialGTMSent, setInitialGTMSent] = useState(false);
  const [
    isRegistrationCostChangedModalOpen,
    setIsRegistrationCostChangedModalOpen,
  ] = useState(false);
  const priceDetailRef = useRef(null);
  const priceDetailHeightRef = useRef(0);
  const isB2BLeasysSelected = useIsB2BLeasysSelected();

  const {
    t,
    i18n: { language },
  } = useTranslation();
  const appConfig = useAppConfigSelector();
  const scrappage = useJourneySpecificScrappage();
  const dispatch = useDispatch();
  const deal = useDealSelector();
  const formatDeal = useFormattedDeal();
  const defaultPreconfiguration = useDefaultPreconfiguration();
  const stepAwareCashData = useStepAwareCashData(true);
  const getBrandIdRedirect = useGetBrandIdRedirect();
  const dateFormat = useDateFormatSelector();
  const brandIdRedirectToBasket = getBrandIdRedirect(ROUTES.BASKET);
  const brandIdRedirectToBasketPath = getBrandIdRedirect(
    ROUTES.BASKET,
    null,
    language,
    false
  );
  const { changeEmailUrl } = useBrandIdSettingsSelector();
  const isAllowedToContinue = useIsAllowedToContinueWithOrder();

  const discountStripe = useDiscountStripeSelector();
  const ecoBonus = useEcoBonusSelector();
  const isDiscountStripeEnabled = discountStripe.enabled;

  const movesProgram = useMovesProgramSelector();
  const isMovesProgramEnabled = movesProgram?.enabled;

  const isCEESubsidyEnabled = useIsAnyCEESubsidyEnabledForJourney();

  const formattedDeal = formatDeal(deal);
  const { userProfile, financeSimulation, currentDeal } = formattedDeal;

  const isIndirectSalesEnabled = useIsIndirectSalesEnabledSelector();

  const {
    basketState,

    closeAll,

    openSummary,
    toggleSummary,
    openDelivery,
    closeDelivery,
    openPersonalInfo,
    closePersonalInfo,
    openVehicleRegistration,
    closeVehicleRegistration,

    isShareConfigModalOpen,
    setIsShareConfigModalOpen,

    isMyDetailsConfirmed,
    confirmDetails,
    unconfirmDetails,

    isHomeDelivery,
    setIsHomeDelivery,

    deliveryAddress,
    setDeliveryAddress,

    isBasketUiConfirming,
    startBasketConfirming,
    stopBasketConfirming,
  } = useBasketState(language, formattedDeal);

  const {
    isSummaryOpen,
    isDeliveryOpen,
    isPersonalInformationOpen,
    isVehicleRegistrationOpen,
  } = basketState;

  const availableVehicles = useCarSelector();
  const router = useRouter();
  const { currency } = availableVehicles;
  const selectedConfiguration = useCarConfigurationSelector();
  const basketErrorCodes = useBasketErrorCodesSelector();
  const expectedDeliveryDate = getExpectedDeliveryDate(formattedDeal);
  const journey = useSelectedJourneySelector();

  const isB2BSelected = useIsB2BSelected();
  const routingUtils = useRoutingUtils();
  const configurationPathname = useConfigurationPathname();

  const pageVariant = getFinanceVariant(financeSimulation.journey);
  const affiliation = useReferrerSelector();
  const token = useTokenSelector();
  const currentDealState = useCurrentDealStateSelector();
  const isUserTrusted = useIsUserTrustedSelector();
  const isGuestCheckoutEnabled = useIsGuestCheckoutEnabledSelector();
  const lastCta = useLastCtaSelector();
  const isRedirectedFromBrandId = useIsRedirectedFromBrandIdSelector();
  const removeQueryParameters = useRemoveQueryParameters();
  const { isDesktop } = useDetectDevice();
  const getErrorMessage = useGetErrorMessage();
  const financeWidgetErrors = useFinanceWidgetErrorsSelector();
  const currencySettings = useCurrencySettingsSelector();
  const vehicleQuantity = useVehicleQuantitySelector();
  const businessModel = useBusinessModelSelector();

  const { multipleVehicles: quantitySettings } = getSolJourneyConfigForJourney(
    journey,
    businessModel,
    appConfig.configuration
  );

  const {
    amount,
    isLeasingRequestLoading,
    isFinanceRequestError,
    withFinanceWidgetButton,
    withEcoBonus,
    deposit,
    teaser,
    isPersonalisedVACOffer,
  } = useGetPriceDetailProps(true);

  const availableRegistrationMethods = useGetAvailableRegistrationMethods();

  const isCitroenRegistrationMethodAvailable = availableRegistrationMethods.some(
    registrationMethod =>
      registrationMethod.method === VEHICLE_REGISTRATION_METHOD.CITROEN
  );

  const { totalPrice } = financeSimulation;

  const {
    isDeliveryMethodUnavailable,
    isDeliveryPointUnavailable,
    isInvalidPreconfiguration,
    deliveryStateMachineKey,
    unavailableItems,
  } = useCarValidation(deal);

  const isDealValid =
    !isDeliveryMethodUnavailable &&
    !isDeliveryPointUnavailable &&
    !isInvalidPreconfiguration;

  const isDeliveryIncluded = getIsDeliveryPriceIncluded(
    router.pathname,
    lastCta
  );

  const isDeliverySelected = Boolean(userProfile.deliveryData.id);

  if (priceDetailRef.current) {
    const { height } = priceDetailRef.current.getBoundingClientRect();
    priceDetailHeightRef.current = height;
  }

  const getFinancingErrorMessages = () => {
    if (isInvalidPreconfiguration) {
      return [];
    }

    if (
      isCashJourneySelected &&
      basketErrorCodes.includes(REQUEST_ERROR_CODES.CASH_PRICE_CHANGED)
    ) {
      return [getErrorMessage(REQUEST_ERROR_CODES.CASH_PRICE_CHANGED)];
    }

    if (isLLDJourneySelected) {
      if (
        basketErrorCodes.includes(REQUEST_ERROR_CODES.FINANCE_PRICE_CHANGED)
      ) {
        return financeWidgetErrors
          .concat([REQUEST_ERROR_CODES.FINANCE_PRICE_CHANGED])
          .map(getErrorMessage);
      }

      return financeWidgetErrors.map(getErrorMessage);
    }
  };

  const getDeliveryErrorMessage = () => {
    if (isDeliveryMethodUnavailable) {
      return getErrorMessage(REQUEST_ERROR_CODES.DELIVERY_METHOD_UNAVAILABLE);
    }

    if (isDeliveryPointUnavailable) {
      return getErrorMessage(REQUEST_ERROR_CODES.DELIVERY_POINT_UNAVAILABLE);
    }

    return null;
  };

  const onVehicleConfirmation = () => {
    stopBasketConfirming();

    closeAll();
    openDelivery();
  };

  const onRequestError = () => {
    stopBasketConfirming();
  };

  const triggerRequest = async () => {
    startBasketConfirming();

    await api.updateSession({ isIndirectSales: isIndirectSalesEnabled });
    dispatch(setIndirectSalesAction(isIndirectSalesEnabled));

    return dispatch(
      postDealAction(CTA_SOL_TYPES.VEHICLE_CONFIRMED, DEAL_TYPE.DEAL, null)
    );
  };

  const handleFinancingModification = async () => {
    openSummary();
    await dispatch(updateLastCtaAction(CTA_SOL_TYPES.VEHICLE_SELECTED));

    gtm.fire(revertProgressEvent(STEPS.FINANCING));
  };

  // Revert lastStepCode when user goes back through Basket
  // we use revertedCta to determine into which state user goes back to.
  const handleModificationRevert = async revertedCta => {
    // Don't try to update last step code if we have no deal (user did not login yet)
    if (!token) {
      return;
    }
    // TODO marek.mika Add condition to allow change only when 'modifiedSection'
    // TODO is not current Active section

    // TODO Also when this function gets called, we should update which section is active

    const leadState = {
      ctaAction: revertedCta,
      dealType: currentDealState,
    };

    try {
      await Promise.all([
        dispatch(updateLastCtaAction(revertedCta)),
        api.updateLastStepCode(token, leadState),
      ]);
    } catch (error) {
      if (error.response.status === 401) {
        dispatch(resetDealAction(true));
        dispatch(setIsTrustedAction(false));

        router.push(ROUTES.CONFIG, configurationPathname);
      }
    }
  };

  const onUserDetailsEditingStart = useCallback(
    async isGtmFireRequested => {
      if (isGtmFireRequested) {
        gtm.fire(revertProgressEvent(STEPS.PERSONAL_DETAILS));
      }

      if (
        [
          CTA_SOL_TYPES.DELIVERY_POINT_SELECTED,
          CTA_SOL_TYPES.PERSONAL_DETAILS_CONFIRMED,
          CTA_SOL_TYPES.CONFIGURATION_SAVED_BY_USER,
        ].includes(lastCta)
      ) {
        closeAll();
        openPersonalInfo();
        unconfirmDetails();

        return handleModificationRevert(CTA_SOL_TYPES.DELIVERY_POINT_SELECTED);
      }
    },
    [lastCta]
  );

  const handleDeliveryModification = async () => {
    closeAll();
    openDelivery();
    dispatch(removeBasketErrorAction(REQUEST_ERROR_CODES.LEAD_TIME_CHANGED));
    await handleModificationRevert(CTA_SOL_TYPES.VEHICLE_CONFIRMED);
  };

  const onBackButtonClick = async () => {
    onReturnButtonClick?.();

    if (isInvalidPreconfiguration) {
      dispatch(updateCarPreconfigurationAction(defaultPreconfiguration));
    }

    await handleModificationRevert(CTA_SOL_TYPES.VEHICLE_SELECTED);
    gtm.fire(revertProgressEvent(STEPS.BASKET));
    await dispatch(updateIsSelectionConfirmedAction(false));

    router.push(ROUTES.CONFIG, configurationPathname);
  };

  const onUserDetailsConfirmationSuccess = async nextUserProfile => {
    dispatch(setIsDealSavedByUserAction(true));

    dispatch(patchUserProfileAction({ ...userProfile, ...nextUserProfile }));

    await dispatch(
      postDealAction(
        CTA_SOL_TYPES.PERSONAL_DETAILS_CONFIRMED,
        DEAL_TYPE.DEAL,
        null
      )
    );

    confirmDetails();
    closeAll();

    if (isIndirectSalesEnabled) {
      // Skip Vehicle Registration - use CITROEN as default
      dispatch(
        setVehicleRegistrationDataAction(VEHICLE_REGISTRATION_METHOD.CITROEN)
      );
      dispatch(calculatePricesAction());

      await dispatch(
        postDealAction(
          CTA_SOL_TYPES.OFFER_CREATED,
          DEAL_TYPE.DEAL,
          DEAL_STATES.OFFER
        )
      );

      await router.push(
        ROUTES.OFFER_SUMMARY,
        routingUtils.getLocalizedPathname(ROUTES.OFFER_SUMMARY, null, language)
      );
    } else {
      openVehicleRegistration();
    }
  };

  const getRegistrationType = () => {
    if (isB2BSelected) {
      return isHomeDelivery
        ? BRANDID_REGISTRATION_TYPES.B2B_HOME_DELIVERY
        : BRANDID_REGISTRATION_TYPES.B2B_DELIVERY_POINT;
    }

    return isHomeDelivery
      ? BRANDID_REGISTRATION_TYPES.HOME_DELIVERY
      : BRANDID_REGISTRATION_TYPES.DELIVERY_POINT;
  };

  useEffect(() => {
    if (currentDeal?.dealState === DEAL_STATES.ORDER) {
      dispatch(createNewDealFromExistingAction());
    }
  }, []);

  useEffect(() => {
    setInitialGTMSent(true);
    getBasketUpdateUrl(
      lastCta,
      pageVariant,
      currency,
      totalPrice,
      selectedConfiguration,
      router.asPath,
      affiliation,
      isUserTrusted,
      isB2BSelected,
      isSSRRendered
    );
  }, [lastCta]);

  // Fetch delivery prices whenever delivery panel is opened, if journey is not cash
  useEffect(() => {
    if (isDeliveryOpen && !isCashJourneySelected && !isSCFJourneySelected) {
      dispatch(fetchAccessoriesIncreaseAction());
    }
  }, [isDeliveryOpen]);

  const shouldBeValidateButtonVisible =
    isSummaryOpen &&
    Object.keys(basketState)
      .filter(objKey => objKey !== "isSummaryOpen")
      .every(key => basketState[key] === false);

  const handleDeliveryConfirm = () => {
    closeAll();
    openPersonalInfo();
    unconfirmDetails();
  };

  const {
    financingLLDData,
    financingLOAData,
    financingVACData,
    financingSCFData,
  } = useCurrentFinancingData();

  useEffect(() => {
    // If we were already at the end we dont need to switch lastCtas
    if (lastCta === CTA_SOL_TYPES.PERSONAL_DETAILS_CONFIRMED) {
      return;
    }
    if (isUserTrusted && isPersonalInformationOpen) {
      // Make sure we run the update only once
      dispatch(setIsRedirectedFromBrandIdAction(false));
      dispatch(
        postDealAction(CTA_SOL_TYPES.DELIVERY_POINT_SELECTED, DEAL_TYPE.DEAL)
      );
    }
  }, [isPersonalInformationOpen, isRedirectedFromBrandId]);

  useEffect(() => {
    removeQueryParameters(["state"]);
  }, []);

  useEffect(() => {
    if (isDealValid) {
      return;
    }

    closeAll();

    if (isInvalidPreconfiguration) {
      openSummary();

      return;
    }

    if (isDeliveryMethodUnavailable || isDeliveryPointUnavailable) {
      openDelivery();
    }
  }, [
    isDeliveryMethodUnavailable,
    isDeliveryPointUnavailable,
    isInvalidPreconfiguration,
    isDealValid,
  ]);

  const openShareConfigModal = ignoreGTM => {
    if (!ignoreGTM) {
      gtm.fire(
        shareConfigurationEmailEvent({
          actionBase: "Display",
          label: t("basket.shareConfigurationModal.button"),
        })
      );
    }
    setIsShareConfigModalOpen(true);
  };

  const closeShareConfigModal = ignoreGTM => {
    if (!ignoreGTM) {
      gtm.fire(
        shareConfigurationEmailEvent({
          actionBase: "Close",
          label: "close",
        })
      );
    }

    setIsShareConfigModalOpen(false);
  };

  const onChangeEmailButtonClick = () => {
    const culture = getCulture(language, COUNTRY);

    window.location.href = `${getUrlWithoutTrailingSlash(
      changeEmailUrl
    )}?return_to=${brandIdRedirectToBasket}&culture=${culture}`;
  };

  const updateVehicleRegistration = async nextRegistration => {
    gtm.fire(
      basketRegistrationChangeEvent({
        label: nextRegistration,
      })
    );

    dispatch(setVehicleRegistrationDataAction(nextRegistration));

    dispatch(calculatePricesAction());
  };

  const onVehicleRegistrationSelected = event =>
    updateVehicleRegistration(event.target.id);

  const onVehicleRegistrationConfirmed = async () => {
    const isSingleRegistrationMethodAvailable =
      availableRegistrationMethods.length === 1;

    if (!isSingleRegistrationMethodAvailable) {
      return;
    }

    const [registrationMethod] = availableRegistrationMethods;

    updateVehicleRegistration(registrationMethod.method);
  };

  const total = useCashVehiclePrice(true);
  const isUIv2 = useIsUIv2Selector();

  const isGB = getIsCountry(COUNTRIES.GB);

  const priceDetailPositionerRef = useRef(null);

  const isFixed = useIsFixed(() => {
    if (!priceDetailPositionerRef.current) {
      return false;
    }

    const { y } = priceDetailPositionerRef.current.getBoundingClientRect();

    return y < 0;
  });

  const wrapperAttributes = useMemo(
    () => ({
      [FIXED_ELEMENTS_ATTRIBUTE_NAME]: isFixed,
    }),
    [isFixed]
  );

  const handleOpenRegistrationCostChangedModal = () => {
    setIsRegistrationCostChangedModalOpen(true);
  };

  const handleCloseRegistrationCostChangedModal = () => {
    setIsRegistrationCostChangedModalOpen(false);
  };

  return (
    <>
      <RegistrationCostChangedModal
        isOpen={isRegistrationCostChangedModalOpen}
        onClose={handleCloseRegistrationCostChangedModal}
      />
      <ProgressBar activeStage={lastCta} />
      <ContentWrapper dataId="basket">
        <div className="w-full text-black grid grid-template-basket gap-4 lg:gap-x-8 py-8 relative">
          <div
            className="max-sm:hidden grid-area-goBack"
            data-id="goback-wrapper"
          >
            <SimpleButton
              className="font-bold text-primaryDark hover:text-black m-0 p-2"
              dataId="basket-back-button"
              onClick={onBackButtonClick}
              withArrow
            >
              {t("basket.back")}
            </SimpleButton>
          </div>
          <div
            className="grid-area-summary flex flex-col gap-8 justify-start min-w-0"
            data-id="summary-wrapper"
          >
            <DealSummary
              deal={formattedDeal}
              errorMessage={
                isInvalidPreconfiguration
                  ? getErrorMessage(
                      REQUEST_ERROR_CODES.PRECONFIGURATION_UNAVAILABLE
                    )
                  : null
              }
              isOpen={isSummaryOpen}
              isQuantityEditable
              onChange={toggleSummary}
              onModifyButtonClick={onBackButtonClick}
              unavailableItems={unavailableItems}
              withModifyButton
              withPromoCodeForm
              withUpsells
            />

            <ErrorBoundary>
              <Financing
                deal={formattedDeal}
                cashData={stepAwareCashData}
                lldData={financingLLDData}
                loaData={financingLOAData}
                scfData={financingSCFData}
                vacData={financingVACData}
                className={!isSummaryOpen && "lg:hidden"}
                disabled={isDesktop}
                errorMessages={getFinancingErrorMessages()}
                isLoading={isLeasingRequestLoading}
                isCurrentStep={lastCta === CTA_SOL_TYPES.VEHICLE_SELECTED}
                isB2BSelected={isB2BSelected}
                isModifyVisible={false}
                isQuantityVisible={quantitySettings.enabled}
                journey={journey}
                opened={isSummaryOpen}
                revertProgress={handleFinancingModification}
                vehicleQuantity={vehicleQuantity}
                withDeliveryInfo={!isDeliverySelected || !isDeliveryIncluded}
              />
            </ErrorBoundary>

            {shouldBeValidateButtonVisible && (
              <div className="flex flex-col items-center w-full gap-2 pb-1">
                <StickyRequestButton
                  data-id="delivery-confirmation-continue-button"
                  disabled={isInvalidPreconfiguration || !isAllowedToContinue}
                  fixedHeaderHeight={priceDetailHeightRef.current}
                  isLoading={isBasketUiConfirming}
                  isStickyToBottom={true}
                  isStickyToTop={true}
                  onRequestError={onRequestError}
                  onRequestSuccess={onVehicleConfirmation}
                  triggerRequestCallback={triggerRequest}
                >
                  {t("basket.confirmOrder")}
                </StickyRequestButton>
                <Button
                  data-id="open-share-config-modal"
                  disabled={isInvalidPreconfiguration}
                  onClick={() => openShareConfigModal(false)}
                  size="small"
                  variant="secondary"
                >
                  {t("basket.shareConfigurationModal.button")}
                </Button>
              </div>
            )}

            <ErrorBoundary>
              <Delivery
                affiliation={affiliation}
                closeDelivery={closeDelivery}
                deliveryAddress={deliveryAddress}
                deliverySelected={
                  isPersonalInformationOpen || isVehicleRegistrationOpen
                }
                disabled={
                  isInvalidPreconfiguration ||
                  [CTA_SOL_TYPES.VEHICLE_SELECTED].includes(lastCta)
                }
                errorMessage={
                  basketErrorCodes.includes(
                    REQUEST_ERROR_CODES.LEAD_TIME_CHANGED
                  )
                    ? getErrorMessage(REQUEST_ERROR_CODES.LEAD_TIME_CHANGED)
                    : null
                }
                expectedDeliveryDate={dayjs(expectedDeliveryDate).format(
                  dateFormat
                )}
                financeSimulation={deal.financeSimulation}
                fixedHeaderHeight={priceDetailHeightRef.current}
                initialDeliveryPoints={deliveryPoints}
                isHomeDelivery={isHomeDelivery}
                isOpen={isDeliveryOpen}
                isSSRRendered={isSSRRendered}
                onConfirmSuccess={handleDeliveryConfirm}
                onModification={handleDeliveryModification}
                openDelivery={openDelivery}
                primaryScreenErrorMessage={getDeliveryErrorMessage()}
                setDeliveryAddress={setDeliveryAddress}
                setIsHomeDelivery={setIsHomeDelivery}
                stateMachineKey={deliveryStateMachineKey}
              />
            </ErrorBoundary>

            <ErrorBoundary>
              {isUserTrusted && userProfile.customerId ? (
                <CollapsableContainer
                  isDisabled={
                    [
                      CTA_SOL_TYPES.VEHICLE_SELECTED,
                      CTA_SOL_TYPES.VEHICLE_CONFIRMED,
                    ].includes(lastCta) || !isDealValid
                  }
                  isOpen={isPersonalInformationOpen}
                  onChange={
                    isPersonalInformationOpen
                      ? closePersonalInfo
                      : openPersonalInfo
                  }
                  title={t("basket.personalDetails.selectTitle")}
                  titleIndex={STEP_INDEXES.PERSONAL_DETAILS}
                >
                  <UserDetailsContainer
                    confirmationButtonLabel={t("general.continue")}
                    defaultValues={userProfile}
                    fixedHeaderHeight={priceDetailHeightRef.current}
                    isBillingAddressVisible
                    isBillingAddressRequired
                    isCompanyInformationVisible={isB2BSelected}
                    isCompanyInformationRequired={isB2BSelected}
                    isConfirmationButtonVisible={!isMyDetailsConfirmed}
                    isDeliveryAddressVisible={
                      deal.userProfile.deliveryData.type ===
                        DELIVERY_TYPE.HOME && !isB2BLeasysSelected
                    }
                    isDeliveryAddressRequired={
                      isHomeDelivery && !isB2BLeasysSelected
                    }
                    isRegistrationAddressRequired={
                      isCitroenRegistrationMethodAvailable
                    }
                    isRegistrationAddressVisible={
                      isCitroenRegistrationMethodAvailable
                    }
                    onEditingStart={onUserDetailsEditingStart}
                    onEmailButtonClick={onChangeEmailButtonClick}
                    onSubmitSuccessCallback={onUserDetailsConfirmationSuccess}
                    returnUrlAfterLogin={brandIdRedirectToBasket}
                    withConsents
                    withDividers
                  />
                </CollapsableContainer>
              ) : isGuestCheckoutEnabled ? (
                <UserAuthentification
                  containerOnChange={
                    isPersonalInformationOpen
                      ? closePersonalInfo
                      : openPersonalInfo
                  }
                  disabled={
                    [
                      CTA_SOL_TYPES.VEHICLE_SELECTED,
                      CTA_SOL_TYPES.VEHICLE_CONFIRMED,
                    ].includes(lastCta) || !isDealValid
                  }
                  index={STEP_INDEXES.PERSONAL_DETAILS}
                  isCurrentStep={
                    lastCta === CTA_SOL_TYPES.DELIVERY_POINT_SELECTED
                  }
                  isOpen={isPersonalInformationOpen}
                  onEditingStart={onUserDetailsEditingStart}
                  onSubmitSuccessCallback={onUserDetailsConfirmationSuccess}
                  registrationType={getRegistrationType()}
                  redirectLogin={brandIdRedirectToBasket}
                />
              ) : (
                <RedirectBrandIdForms
                  disabled={
                    [
                      CTA_SOL_TYPES.VEHICLE_SELECTED,
                      CTA_SOL_TYPES.VEHICLE_CONFIRMED,
                    ].includes(lastCta) || !isDealValid
                  }
                  isCurrentStep={
                    lastCta === CTA_SOL_TYPES.DELIVERY_POINT_SELECTED
                  }
                  isOpen={isPersonalInformationOpen}
                  index={STEP_INDEXES.PERSONAL_DETAILS}
                  registrationType={getRegistrationType()}
                  redirectLogin={brandIdRedirectToBasket}
                  redirectRegistration={brandIdRedirectToBasketPath}
                  containerOnChange={
                    isPersonalInformationOpen
                      ? closePersonalInfo
                      : openPersonalInfo
                  }
                />
              )}
            </ErrorBoundary>

            {!isIndirectSalesEnabled && (
              <ErrorBoundary>
                <VehicleRegistration
                  disabled={
                    [
                      CTA_SOL_TYPES.VEHICLE_SELECTED,
                      CTA_SOL_TYPES.VEHICLE_CONFIRMED,
                      CTA_SOL_TYPES.DELIVERY_POINT_SELECTED,
                    ].includes(lastCta) || !isDealValid
                  }
                  isOpen={isVehicleRegistrationOpen && isDealValid}
                  openVehicleRegistration={openVehicleRegistration}
                  closeVehicleRegistration={closeVehicleRegistration}
                  index={STEP_INDEXES.VEHICLE_REGISTRATION}
                  deal={formattedDeal}
                  availableMethods={availableRegistrationMethods}
                  onVehicleRegistrationSelected={onVehicleRegistrationSelected}
                  onVehicleRegistrationConfirmed={
                    onVehicleRegistrationConfirmed
                  }
                  updateVehicleRegistration={updateVehicleRegistration}
                  initialGTMSent={initialGTMSent}
                  fixedHeaderHeight={priceDetailHeightRef.current}
                  openRegistrationCostChangedModal={
                    handleOpenRegistrationCostChangedModal
                  }
                />
              </ErrorBoundary>
            )}
          </div>
          <div className="grid-area-recap relative" data-id="recap-wrapper">
            <div className="sticky top-53">
              <BasketHeader
                deal={deal}
                withRemainingText={!isInvalidPreconfiguration}
              />
              <div className="md:hidden" ref={priceDetailPositionerRef} />
              {isFixed && (
                <div style={{ height: priceDetailHeightRef.current }} />
              )}
              <div
                className={twMerge(
                  isFixed && "max-lg:fixed max-lg:top-0",
                  "w-full z-30"
                )}
                ref={priceDetailRef}
              >
                {!isInvalidPreconfiguration && (
                  <PriceDetail
                    amount={amount}
                    className={twMerge(
                      "transition duration-300",
                      withEcoBonus ||
                        scrappage.enabled ||
                        isMovesProgramEnabled ||
                        isCEESubsidyEnabled
                        ? "md:rounded-t-md"
                        : "md:rounded-md",
                      isFixed
                        ? "bg-primaryDark/90 backdrop-blur-md"
                        : "bg-primaryDark"
                    )}
                    currencySymbol={currencySettings.symbol}
                    deposit={deposit}
                    forceFinancingSwitch={true}
                    isBasketUi={isUIv2 && isGB}
                    isFinanceRequestError={isFinanceRequestError}
                    isLoading={isLeasingRequestLoading}
                    isPersonalisedVACOffer={isPersonalisedVACOffer}
                    isQuantityVisible={quantitySettings.enabled}
                    journey={journey}
                    onIsFinancingOpenChange={openSummary}
                    priceFormat={currencySettings.format}
                    teaser={teaser}
                    total={total}
                    vehicleQuantity={vehicleQuantity}
                    withDeliveryInfo={
                      !isDeliverySelected || !isDeliveryIncluded
                    }
                    withEcoBonus={withEcoBonus}
                    withFinanceWidgetButton={withFinanceWidgetButton}
                    withJourneySwitch={
                      !lastCta || lastCta === CTA_SOL_TYPES.VEHICLE_SELECTED
                    }
                    withTitle={
                      lastCta && lastCta !== CTA_SOL_TYPES.VEHICLE_SELECTED
                    }
                    withParallelograms
                    {...wrapperAttributes}
                  />
                )}
                {(isDiscountStripeEnabled ||
                  isMovesProgramEnabled ||
                  isCEESubsidyEnabled) && (
                  <DiscountStripe
                    governmentDiscountPercentage={
                      ecoBonus.governmentDiscountPercentage
                    }
                    isFixed={isFixed}
                    isMovesProgramEnabled={isMovesProgramEnabled}
                    isScrappageEnabled={scrappage.enabled}
                    isScrappageSelected={formattedDeal.isScrappageSelected}
                    scrappageDiscountPercentage={scrappage.discountPercentage}
                    withEcoBonus={withEcoBonus}
                  />
                )}
              </div>
            </div>
          </div>
          <ShareConfigModal
            closeModal={closeShareConfigModal}
            isOpen={isShareConfigModalOpen}
          />
        </div>
      </ContentWrapper>
    </>
  );
};

Basket.propTypes = {
  onReturnButtonClick: func,
  deliveryPoints: array,
  isSSRRendered: bool,
};

export default Basket;
